import { useEffect, useRef, useState } from "react";
import { POPUP } from "@/lib/searchConfig";
import { ArrowRight, Sparkles, X } from "lucide-react";

const DISMISSED_KEY = "ozy_popup_dismissed";
const SUBMITTED_KEY = "ozy_lead_submitted";

// Random time window — feels less robotic than a fixed delay
const MIN_TIME_MS = 45000;   // 45 seconds
const MAX_TIME_MS = 120000;  // 2 minutes
const SCROLL_TRIGGER_PCT = 0.40;
const INACTIVE_MS = 60000;

function track(event, params = {}) {
  if (typeof window === "undefined") return;
  if (window.gtag) window.gtag("event", event, params);
  if (window.fbq) {
    if (event === "popup_cta_click") window.fbq("trackCustom", "PopupCtaClick", params);
    else if (event === "popup_impression") window.fbq("trackCustom", "PopupImpression", params);
    else if (event === "popup_dismiss") window.fbq("trackCustom", "PopupDismiss", params);
  }
}

export default function ExitIntent({ onCta, suppress = false }) {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const firedRef = useRef(false);
  const mountedAtRef = useRef(typeof performance !== "undefined" ? performance.now() : Date.now());
  const interactedRef = useRef(false);
  const lastActivityRef = useRef(Date.now());
  const timersRef = useRef([]);
  const footerVisibleRef = useRef(false);
  const isMobileRef = useRef(false);

  const trigger = (reason) => {
    if (firedRef.current) return;
    if (suppress) return;
    // Do not let footer navigation cause a new popup to cover the mobile footer.
    // Synchronous check (avoids IntersectionObserver race with the scroll handler).
    if (isMobileRef.current && typeof document !== "undefined") {
      const f = document.querySelector("footer");
      if (f) {
        const r = f.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) return;
      }
    }
    if (sessionStorage.getItem(DISMISSED_KEY)) return;
    if (localStorage.getItem(SUBMITTED_KEY)) return;
    firedRef.current = true;
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    const time_to_popup_ms = Math.round(now - mountedAtRef.current);
    setOpen(true);
    track("popup_impression", { reason, time_to_popup_ms });
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (suppress) return;
    if (sessionStorage.getItem(DISMISSED_KEY)) return;
    if (localStorage.getItem(SUBMITTED_KEY)) return;

    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    isMobileRef.current = isMobile;

    // 1. Random delay between 45s–2min
    const delay = Math.floor(MIN_TIME_MS + Math.random() * (MAX_TIME_MS - MIN_TIME_MS));
    const tTimer = setTimeout(() => trigger("time_random"), delay);
    timersRef.current.push(tTimer);

    // 2. Scroll-depth
    const onScroll = () => {
      lastActivityRef.current = Date.now();
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      if (docH <= 0) return;
      if (window.scrollY / docH >= SCROLL_TRIGGER_PCT) trigger("scroll_40pct");
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // 3. Engagement (after meaningful interaction + 12s)
    const onInteract = () => {
      lastActivityRef.current = Date.now();
      if (!interactedRef.current) {
        interactedRef.current = true;
        const t = setTimeout(() => trigger("engaged"), 12000);
        timersRef.current.push(t);
      }
    };
    window.addEventListener("click", onInteract, { passive: true });
    window.addEventListener("keydown", onInteract);
    window.addEventListener("touchstart", onInteract, { passive: true });

    // 4a. Desktop exit-intent
    const onMouseLeave = (e) => { if (e.clientY <= 0) trigger("exit_intent_desktop"); };
    if (!isMobile) document.addEventListener("mouseleave", onMouseLeave);

    // 4b. Mobile back-button
    let pushedState = false;
    const onPopState = () => trigger("exit_intent_mobile_back");
    if (isMobile) {
      window.history.pushState({ ozy_popup: true }, "");
      pushedState = true;
      window.addEventListener("popstate", onPopState);
    }

    // 4c. Mobile inactivity
    let inactivityCheck;
    if (isMobile) {
      inactivityCheck = setInterval(() => {
        if (Date.now() - lastActivityRef.current > INACTIVE_MS) trigger("inactive_mobile");
      }, 5000);
    }

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("click", onInteract);
      window.removeEventListener("keydown", onInteract);
      window.removeEventListener("touchstart", onInteract);
      document.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("popstate", onPopState);
      if (pushedState && window.history.state?.ozy_popup) {
        try { window.history.back(); } catch (e) { /* noop */ }
      }
      if (inactivityCheck) clearInterval(inactivityCheck);
    };
  }, [suppress]);

  const close = (kind) => {
    setClosing(true);
    sessionStorage.setItem(DISMISSED_KEY, "1");
    track(kind === "cta" ? "popup_cta_click" : "popup_dismiss", {});
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
      if (kind === "cta") onCta?.();
    }, 250);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Indicative offer"
      data-testid="smart-popup"
      className={`fixed z-[60] pointer-events-none
        inset-x-3 bottom-3 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[min(380px,calc((100vw-42rem)/2-2.5rem))] sm:min-w-[260px]`}
    >
      <div
        className={`pointer-events-auto relative overflow-hidden rounded-2xl border border-[#E50914]/35 bg-[#0a0a0a]/95 backdrop-blur-2xl shadow-[0_30px_80px_-10px_rgba(0,0,0,0.9),0_0_0_1px_rgba(229,9,20,0.10)]
          transition-all duration-300 ease-out
          ${closing ? "opacity-0 translate-y-3 sm:translate-y-0 sm:translate-x-6" : "opacity-100 translate-y-0 translate-x-0"}
        `}
        style={{ animation: closing ? undefined : "ozy-slide-in 380ms cubic-bezier(0.22, 1, 0.36, 1) both" }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-40"
          style={{ background: "radial-gradient(closest-side, #E50914, transparent 70%)" }}
        />
        <span aria-hidden="true" className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />

        <button
          type="button"
          onClick={() => close("dismiss")}
          data-testid="smart-popup-close"
          aria-label="Close"
          className="absolute top-2.5 right-2.5 w-7 h-7 flex items-center justify-center rounded-full text-zinc-500 hover:text-white hover:bg-white/5 transition-colors z-10"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <div className="relative p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF3B47] to-[#A50710] flex items-center justify-center shadow-[0_4px_12px_rgba(229,9,20,0.4)]">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#E50914] font-semibold">
              {POPUP.eyebrow}
            </span>
          </div>

          <p className="font-bebas text-3xl sm:text-4xl leading-none tracking-wide mb-2" data-testid="popup-punchline">
            <span className="text-[#E50914] font-bold">Funding.</span> <span className="text-white font-bold">Found.</span>
          </p>
          <h3 className="text-white text-base font-semibold leading-snug mb-1.5" data-testid="popup-title">
            {POPUP.title}
          </h3>
          <p className="text-zinc-400 text-[13px] leading-relaxed mb-4" data-testid="popup-body">
            {POPUP.body}
          </p>

          <button
            type="button"
            onClick={() => close("cta")}
            data-testid="exit-intent-cta"
            className="w-full h-10 inline-flex items-center justify-center gap-1.5 bg-[#E50914] hover:bg-[#F6121D] text-white font-semibold text-sm rounded-full shadow-[0_6px_20px_rgba(229,9,20,0.4)] transition-colors"
          >
            {POPUP.cta} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
